import { useQueries } from "@tanstack/react-query";
import { ErrorPage, Loader, NoInternetWrapper } from "components";
import {
  CityResponseInterface,
  ClientResponseInterface,
  CompaniesResponseInterface,
  CountryResponseInterface,
  EmployeeResponseInterface,
  FamilyResponseInterface,
  PartnerResponseInterface,
  PortfolioGroupResponseInterface,
  PortfolioResponseInterface,
  StateResponseInterface,
} from "interfaces/PrivateRouteInterface";
import CompanyNameModal from "Pages/CompanyInformation/CompanyNameModal";
import { ErrorBoundary } from "react-error-boundary";
import { Helmet } from "react-helmet-async";
import { Outlet, useLocation } from "react-router-dom";
import {
  CITY_LIST,
  CLIENT_LIST,
  COMPANIES,
  COUNTRY_LIST,
  EMPLOYEE_LIST,
  FAMILY_LIST,
  FEE_PLAN_LIST,
  INVESTOR_ON_BOARDING_FLOW,
  IS_FIRST_LOGIN,
  ON_BOARDING_FLOW,
  PARTNER_LIST,
  PORTFOLIO_GROUP_LIST,
  PORTFOLIOS,
  STATE_LIST,
} from "store/slices/appSlice";
import { setBrokerList } from "store/slices/portfolioSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import {
  GET_ALL_EMPLOYEE,
  GET_ALL_FAMILY,
  GET_ALL_FEE_PLAN,
} from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { QueryKeys } from "utils/queryKey";

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
}) => <ErrorPage error={error} resetErrorBoundary={resetErrorBoundary} />;

const AppLayout: React.FC = () => {
  const userLoading = useAppSelector((state) => state?.user?.userLoading);
  const isFirstLogin = useAppSelector((state) => state?.app?.isFirstLogin);
  const selectedCountry = useAppSelector(
    (state) => state?.app?.selectedCountry,
  );
  const selectedState = useAppSelector((state) => state?.app?.selectedState);

  const dispatch = useAppDispatch();
  const location = useLocation();

  const isCalledApi =
    Boolean(localStorage.getItem("token")) &&
    !location.pathname?.includes("/broker-integration") &&
    location?.pathname !== "client-risk-profile" &&
    !location.pathname?.includes("/broker-integration/order") &&
    !location.pathname?.includes("/connect-client-payment-gateway") &&
    !location.pathname?.includes("/broker-integration/broker");

  useQueries({
    queries: [
      {
        queryKey: [QueryKeys.PORTFOLIOS_SHORT_DATA],
        queryFn: () => axiosInstance.get(`/portfolios/all/`),
        enabled: isCalledApi,
        onSuccess: (data: PortfolioResponseInterface) => {
          dispatch(PORTFOLIOS(data?.data));
        },
      },
      {
        queryKey: ["FETCH_COMPANIES"],
        queryFn: () =>
          axiosInstance.get("/instruments/list/", {
            params: {
              inst_type: JSON.stringify([
                "EQ",
                "INDEX",
                "ETF",
                "BOND",
                "MF",
                "CASH",
                "PEQ",
                "GEQ",
                "FD",
                "AIF",
                // "OPTIONS",
                // "FUTURES",
              ]),
            },
          }),
        enabled: isCalledApi,
        onSuccess: (data: CompaniesResponseInterface) => {
          dispatch(COMPANIES(data?.data?.data));
        },
      },
      {
        queryKey: ["FETCH_ALL_PORTFOLIO_GROUP"],
        queryFn: () => axiosInstance.get("/portfolio-groups/all/?role=advisor"),
        enabled: isCalledApi,
        onSuccess: (data: PortfolioGroupResponseInterface) => {
          dispatch(PORTFOLIO_GROUP_LIST(data?.data));
        },
      },
      {
        queryKey: [QueryKeys.CLIENTS_SHORT_DATA],
        queryFn: () => axiosInstance.get(`/client/all/`),
        enabled: isCalledApi,
        onSuccess: (data: ClientResponseInterface) => {
          dispatch(CLIENT_LIST(data?.data));
        },
      },
      {
        queryKey: [QueryKeys.EMPLOYEE_LIST],
        queryFn: () => axiosInstance.get(GET_ALL_EMPLOYEE),
        enabled: isCalledApi,
        onSuccess: (data: EmployeeResponseInterface) => {
          dispatch(EMPLOYEE_LIST(data?.data));
        },
      },
      {
        queryKey: [QueryKeys.FAMILY_LIST],
        queryFn: () => axiosInstance.get(GET_ALL_FAMILY),
        enabled: isCalledApi,
        onSuccess: (data: FamilyResponseInterface) => {
          dispatch(FAMILY_LIST(data?.data));
        },
      },
      {
        queryKey: ["GET_ALL_COUNTRIES"],
        queryFn: () => axiosInstance.get("/country/"),
        enabled: isCalledApi,
        onSuccess: (response: CountryResponseInterface) => {
          dispatch(COUNTRY_LIST(response?.data?.data));
        },
      },
      {
        queryKey: ["GET_STATE", selectedCountry],
        queryFn: () =>
          axiosInstance.get(`/state/?country_id=${selectedCountry}`),
        enabled: isCalledApi && Boolean(selectedCountry),
        onSuccess: (response: StateResponseInterface) => {
          dispatch(STATE_LIST(response?.data?.data));
        },
      },
      {
        queryKey: ["GET_CITY", selectedState],
        queryFn: () => axiosInstance.get(`/city/?state_id=${selectedState}`),
        enabled: Boolean(selectedState) && isCalledApi,
        onSuccess: (response: CityResponseInterface) => {
          dispatch(CITY_LIST(response?.data?.data));
        },
      },
      {
        queryKey: ["FETCH_ALL_PARTNER"],
        queryFn: () => axiosInstance.get("/partner/all/"),
        enabled: isCalledApi,
        onSuccess: (data: PartnerResponseInterface) => {
          dispatch(PARTNER_LIST(data?.data));
        },
      },
      {
        queryKey: ["advisor_initiated_advisory_flow"],
        queryFn: () =>
          axiosInstance.get(
            "/onboardflow/?flow_type=advisor_initiated_advisory_flow",
          ),
        enabled: isCalledApi,
        onSuccess: (data: any) => {
          dispatch(ON_BOARDING_FLOW(data?.data?.data));
        },
      },
      {
        queryKey: ["GET_FEE_PLANS_DROPDOWN"],
        queryFn: () => axiosInstance.get(GET_ALL_FEE_PLAN),
        enabled: isCalledApi,
        onSuccess: (data: any) => {
          dispatch(FEE_PLAN_LIST(data?.data?.data));
        },
      },
      {
        queryKey: ["client_initiated_advisory_flow"],
        queryFn: () =>
          axiosInstance.get(
            "/onboardflow/?flow_type=client_initiated_advisory_flow",
          ),
        enabled: isCalledApi,
        onSuccess: (data: any) => {
          dispatch(INVESTOR_ON_BOARDING_FLOW(data?.data?.data));
        },
      },
      {
        queryKey: ["BROKER_LIST"],
        queryFn: () => axiosInstance.get("/user-details/broker-support/"),
        enabled: isCalledApi,
        onSuccess: (response: {
          data: { data: { name: string; value: string }[] };
        }) => {
          dispatch(setBrokerList(response?.data?.data));
        },
      },
      // {
      //   queryKey: ["TAG_MANAGER"],
      //   queryFn: () => axiosInstance.get("/tags/"),
      //   enabled: isCalledApi,
      //   onSuccess: (response: { data: { data: TagInterface[] } }) => {
      //     dispatch(TAG(response?.data?.data));
      //   },
      // },
    ],
  });

  const closeModal = () => {
    dispatch(IS_FIRST_LOGIN(!isFirstLogin));
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        {userLoading ? (
          <Loader />
        ) : (
          <NoInternetWrapper>
            <Outlet />
          </NoInternetWrapper>
        )}
      </ErrorBoundary>

      {isFirstLogin && <CompanyNameModal open setOpen={closeModal} />}
    </>
  );
};

export default AppLayout;
